<template>
  <b-col
    ref="contentScrollable"
    class="m-0 p-0 content-col"
    @scroll="handleScroll"
  >
    <BackToTopButton
      :is-visible="isBackToTopVisible"
      @scrollToTop="scrollToTop"
    />
    <div
      v-if="isLoading"
      class="w-100 text-center my-3"
    >
      <b-spinner
        variant="success"
        style="width: 3rem; height: 3rem;"
        label="Large Spinner Text Centered"
      /> Loading...
    </div>
    <div
      v-if="state === CLEAN_STATE"
      class="container"
    >
      <div class="m-2">
        <b-breadcrumb>
          <b-breadcrumb-item to="/navigation">
            <b-icon icon="house-fill" />My
            Clusters
          </b-breadcrumb-item>
          <b-breadcrumb-item
            v-if="routeList.length > 0"
            :to="`/navigation/${routeList[0]}`"
            :active="false"
          >
            {{ breadcrumbClusterName }}
          </b-breadcrumb-item>
          <b-breadcrumb-item
            v-if="routeList.length > 1"
            :to="`/navigation/${routeList[0]}/${routeList[1]}`"
            :active="false"
          >
            {{ breadcrumbProjectName }}
          </b-breadcrumb-item>
        </b-breadcrumb>
      </div>
      <div class="my-2 d-flex justify-content-center align-items-center">
        <div class="loading-report-container">
          <h2 class="welcome-message-header">
            Welcome to Djuli
          </h2>
          <h4
            v-if="!isMessageVisibleReportViewer"
            class="no-report-header"
          >
            No report opened yet
          </h4>
          <p v-if="!isMessageVisibleReportViewer">
            Please use the navigation links above or on the left to open a report.
          </p>
          <p
            v-else
            class="message-text mt-4 ml-5"
          >
            <b-spinner
              variant="primary"
              label="Spinning"
              class="spinner-message mr-3"
            />{{ getMessageTextReportViewer }}
          </p>
          <img
            class="djuliLogo-mssg"
            alt="djuliLogo"
            src="../../assets/MainMenu/djuliImage.png"
          >
        </div>
      </div>
    </div>
    <b-container
      v-else-if="state === REPORT_STATE"
      class="h-100 m-2 pl-0 pr-3"
      fluid
    >
      <b-button
        v-if="!isReportListVisReportViewer"
        v-b-tooltip.hover.right="'Expand reports table'"
        size="sm"
        variant="primary"
        class="toggle-reports px-0"
        @click="toggleReportListVisReportViewer"
      >
        <b-icon
          icon="caret-right-fill"
          aria-label="Open Report List"
        />
      </b-button>
      <b-row class="mr-1">
        <b-col cols="auto">
          <b-breadcrumb>
            <b-breadcrumb-item to="/navigation">
              <b-icon icon="house-fill" />My
              Clusters
            </b-breadcrumb-item>
            <b-breadcrumb-item
              v-if="routeList.length > 0"
              :to="`/navigation/${routeList[0]}`"
              :active="false"
            >
              {{ breadcrumbClusterName }}
            </b-breadcrumb-item>
            <b-breadcrumb-item
              v-if="routeList.length > 1"
              :to="`/navigation/${routeList[0]}/${routeList[1]}`"
              :active="false"
            >
              {{ breadcrumbProjectName }}
            </b-breadcrumb-item>
            <b-breadcrumb-item
              v-if="routeList.length > 2"
              class="breadcrumb-link"
              :to="`/navigation/${routeList[0]}/${routeList[1]}/report/${routeList[2]}`"
              :active="true"
            >
              {{ breadcrumbReportName }}
            </b-breadcrumb-item>
          </b-breadcrumb>
        </b-col>
        <b-col>
          <b-row align-h="end">
            <b-col>
              <p
                v-if="isMessageVisibleReportViewer"
                class="message-text-report m-2"
              >
                <b-spinner
                  variant="primary"
                  label="Spinning"
                  class="spinner-message mr-3"
                />{{
                  getMessageTextReportViewer
                }}
              </p>
            </b-col>
            <b-col
              cols="auto"
              md="auto"
              @mouseover="onHover('ReportViewer/editMode')"
              @mouseleave="onLeave"
            >
              <ToggleButton
                v-if="getCurrentReportViewer.accesstype"
                v-model="editMode"
                :value="false"
                :labels="{ checked: 'Edit Mode', unchecked: 'View Mode' }"
                :width="100"
                :height="35"
              />
            </b-col>
          </b-row>
        </b-col>
      </b-row>
      <transition name="fade-slide">
        <b-row
          v-if="editMode"
          align-h="end"
          class="mb-2 mr-2"
        >
          <b-col
            cols="auto"
            class="mr-5"
          >
            <div v-if="isReportModifiedReportViewer">
              <b-button
                variant="success"
                class="m-1"
                size="sm"
                @click="userSaveReport"
              >
                Save
              </b-button>
              <b-button
                variant="danger"
                class="m-1"
                size="sm"
                @click="recoverReport"
              >
                Cancel
              </b-button>
            </div>
          </b-col>
          <b-col
            cols="auto"
            md="auto"
          >
            <b-button
              v-b-tooltip.hover.bottom="'Reset the grid layout'"
              size="sm"
              class="m-1"
              variant="outline-danger"
              @mouseover="onHover('ReportViewer/resetGrid')"
              @mouseleave="onLeave"
              @click="resetGridLayout()"
            >
              <b-icon
                icon="card-heading"
                aria-label="Help"
              />
            </b-button>
          </b-col>
          <b-col
            cols="auto"
            md="auto"
          >
            <b-button
              v-b-tooltip.hover.bottom="'Force update from configuration'"
              size="sm"
              class="m-1"
              variant="outline-primary"
              @mouseover="onHover('ReportViewer/forceUpdate')"
              @mouseleave="onLeave"
              @click="setForceUpdateReportViewer(1)"
            >
              <b-icon
                icon="arrow-repeat"
                aria-label="Help"
              />
            </b-button>
          </b-col>
          <b-col
            md="auto"
            cols="auto"
          >
            <ToolsEdit
              v-if="getCurrentReportViewer.type !== Comparison_Type"
              @reportDeepChange="forceUpdateReportViewer"
            />
          </b-col>
        </b-row>
      </transition>
      <b-container
        ref="exportableZone"
        :class="['w-100 h-100 exportable-zone p-0 m-0']"
        fluid
      >
        <b-row
          ref="reportTextData"
          class="row-data-report m-0 p-0"
        >
          <div
            v-if="isSignatureVisible"
            class="signature-wrapper"
          >
            <p class="signature-text">
              Signature
            </p>
            <div class="signature-area">
              <div class="signature-line" />
            </div>
          </div>
          <ReportTextData
            ref="reportData"
            :editable="editMode"
            @downloadProcessedData="downloadProcessedData"
          />
        </b-row>
        <b-row class="cards-container m-0 p-0">
          <ReportBoard
            ref="reportBoardElem"
            @cardsReloaded="onCardsReloaded"
          />
        </b-row>
        <ConfigurationWindow class="configuration-window" />
      </b-container>
      <ExportPDF class="export-window" />
    </b-container>
    <b-container v-else-if="state=== NO_PERMISSION">
      <b-row>
        <div class="mx-3 my-2">
          <b-breadcrumb>
            <b-breadcrumb-item to="/navigation">
              <b-icon icon="house-fill" />My
              Clusters
            </b-breadcrumb-item>
          </b-breadcrumb>
        </div>
      </b-row>
      <b-row>
        <b-col class="mx-3 col-lg-8 col-md-10 col-sm-12">
          <b-row>
            <b-card class="mb-4 mt-2 custom-card">
              <b-card-header class="d-flex align-items-center p-2">
                <b-card-title class="h4 mb-0 ml-2 text-danger">
                  <b-icon
                    icon="exclamation-triangle"
                    class="text-warning bg-danger"
                  /><span class="font-weight-bold"> Resource Inaccessible!</span>
                </b-card-title>
              </b-card-header>
              <b-card-body class="text-justify">
                <span class="font-weight-bold">Insufficient Permission!</span> Either the owner has not shared the report with the current user or the resource doesn't exist. Please send an email request to the owner to get access to the report.
              </b-card-body>
              <div class="d-flex justify-content-end">
                <b-button
                  variant="primary"
                  @click="showPopup"
                >
                  Send Request
                </b-button>
              </div>
            </b-card>
          </b-row>
          <b-row>
            <div>
              <b-modal
                v-model="showingPopup"
                title="Request Resource Access"
                :header-bg-variant="headerBgVariant"
                hide-footer
              >
                <b-form @submit.stop.prevent="sendAccessRequest">
                  <b-form-group
                    label="Email*:"
                    label-for="email-input"
                    label-cols-sm="4"
                    label-cols-lg="2"
                    class="mb-3"
                    :state="emailValid ? null : false"
                    :invalid-feedback="emailValid ? null : 'Please enter a valid email address.'"
                  >
                    <b-form-input
                      id="email-input"
                      v-model="receiver"
                      required
                      type="email"
                      placeholder="email"
                      disabled
                    />
                  </b-form-group>
                  <b-form-group
                    label="Access to*:"
                    label-for="resource-type"
                    label-cols-sm="4"
                    label-cols-lg="2"
                    class="mb-3"
                  >
                    <b-form-select
                      id="resource-type"
                      v-model="resourceType"
                      :options="resourceItems"
                      placeholder="select resource"
                      required
                    />
                  </b-form-group>
                  <b-form-group
                    label="Message:"
                    label-for="message-textarea"
                    label-cols-sm="4"
                    label-cols-lg="2"
                    class="mb-3"
                  >
                    <b-form-textarea
                      id="message-textarea"
                      v-model="messageBody"
                      rows="5"
                      placeholder="Enter your message"
                    />
                  </b-form-group>
                  <div class="d-flex justify-content-end">
                    <b-button
                      type="submit"
                      variant="primary"
                      class="mx-2"
                    >
                      Send
                    </b-button>
                    <b-button
                      type="button"
                      variant="secondary"
                      @click="closePopup"
                    >
                      Cancel
                    </b-button>
                  </div>
                </b-form>
              </b-modal>
            </div>
          </b-row>
        </b-col>
      </b-row>
    </b-container>
    <b-container v-else>
      <b-row>
        <div class="mx-3 my-2">
          <b-breadcrumb>
            <b-breadcrumb-item to="/navigation">
              <b-icon icon="house-fill" />My
              Clusters
            </b-breadcrumb-item>
          </b-breadcrumb>
        </div>
      </b-row>
      <b-row>
        <b-col class="mx-3 col-lg-8 col-md-10 col-sm-12">
          <b-row>
            <b-card class="mb-4 mt-2 custom-card">
              <b-card-header class="d-flex align-items-center p-2">
                <b-card-title class="h4 mb-0 ml-2 text-danger">
                  <b-icon
                    icon="exclamation-triangle"
                    class="text-warning bg-info"
                  /><span class="font-weight-bold"> No report exists with the current selection!</span>
                </b-card-title>
              </b-card-header>
              <b-card-body class="text-justify">
                <span class="font-weight-bold">Please</span> make sure to select a report that exists and you are authorized to access it.
              </b-card-body>
            </b-card>
          </b-row>
        </b-col>
      </b-row>
    </b-container>
  </b-col>
</template>

<script>
import BackToTopButton from '@/components/common/BackToTopButton'
import ReportBoard from './ReportBoard'
import ToolsEdit from './ToolsEdit'
import ConfigurationWindow from '../configWindow/ConfigurationWindow'
import ReportTextData from './ReportTextData'
import { ToggleButton } from 'vue-js-toggle-button'
import { Comparison_Type, homogenizeReport, getScanFilesList } from '@/common/sharedDefinitions/reportDefinitions';
import { ExtraConfigKey, NameExtraConfig } from '@/common/sharedDefinitions/commonPlotDefinitions'
import { mapGetters, mapActions } from 'vuex'
import ReportInfo from '@/functions/ReportInfo'
import XLSX from 'xlsx'
import ExportPDF from './ExportPDF'
import {AES} from 'crypto-js';
import Utf8 from 'crypto-js/enc-utf8';
import { getAuth } from 'firebase/auth';
import {firebaseApp} from '@/firebaseConfig';

const CLEAN_STATE = 0
const REPORT_STATE = 1
const NOT_EXISTS_STATE = 2
const NO_PERMISSION = 3
const auth = getAuth(firebaseApp);

const zoneNotify = 'Report Viewer'
const loading_Message = 'Loading report...'
const exportingProcData_Message = 'Exporting processed data...'
const autosaving_Message = 'Autosaving the report...'

export default {
  name: 'ReportViewerContent',
  components: {
    BackToTopButton,
    ReportBoard,
    ToggleButton,
    ToolsEdit,
    ReportTextData,
    ConfigurationWindow,
    ExportPDF
  },
  props: {
    updateSidebar: {
      type: Function,
      default: () => {}
    }
  },
  data() {
    return {
      state: CLEAN_STATE,
      CLEAN_STATE,
      REPORT_STATE,
      NOT_EXISTS_STATE,
      NO_PERMISSION,
      routeList: [],
      Comparison_Type: Comparison_Type,
      isSignatureVisible: false,
      showingPopup: false,
      receiver: '',
      messageBody: '',
      emailValid: true,
      headerBgVariant: 'light',
      isLoading: false,
      isBackToTopVisible: false,
      resourceType: 'Cluster',
      resourceItems: ['Cluster', 'Report']
    }
  },
  computed: {
    ...mapGetters([
      'getTokenMiddlewareAuth',
      'isEditModeReportViewer',
      'getCurrentReportViewer',
      'isReportListVisReportViewer',
      'isMessageVisibleReportViewer',
      'getMessageTextReportViewer',
      'isVisibleLearningCntxlHelp',
      'isReportModifiedReportViewer',
      'isSaveLayoutReportViewer',
      'getOriginalConfigurationReportViewer',
      'getNameCurrProjectNavigation',
      'getNameCurrClusterNavigation',
      'isAutoSaveReportViewer',
      'isForceReadFromDBReportViewer',
      'getUserNameMiddlewareAuth',
      'getReportAccessTypeMiddleware',
      'getWidthWindowMainApp',
    ]),
    editMode: {
      set(value) {
        this.setDragModeReportViewer(value)
      },
      get() {
        return this.isEditModeReportViewer
      }
    },
    breadcrumbClusterName() {
      const name = this.getNameCurrClusterNavigation;
      return name && name.length > 20 ? name.slice(0, 20) + '…' : (name || 'Projects');
    },
    breadcrumbProjectName() {
      const name = this.getNameCurrProjectNavigation;
      return name && name.length > 20 ? name.slice(0, 20) + '…' : (name || 'Reports');
    },
    breadcrumbReportName() {
      const name = this.getCurrentReportViewer?.name;
      return name && name.length > 20 ? name.slice(0, 20) + '…' : (name || 'Report');
    },
  },
  watch: {
    isAutoSaveReportViewer(value) {
      if (value) {
        this.setAutoSaveReportViewer(false)
        this.saveReport(autosaving_Message)
      }
    },
    receiver(value) {
      if (value) {
        this.emailValid = true;
      }
    },
  },
  async mounted() {
    if (typeof this.$route.params.reportId !== 'undefined' && this.$route.params.reportId !== null && this.$route.params.reportId !== '') {
      this.setReportURLReportViewer(this.$route.params.reportId);
    }
    if (typeof this.$route.params.projectId !== 'undefined' && this.$route.params.projectId !== null && this.$route.params.projectId !== '') {
      this.setProjectURLReportViewer(this.$route.params.projectId);
    }
    if (typeof this.$route.params.clusterId !== 'undefined' && this.$route.params.clusterId !== null && this.$route.params.clusterId !== '') {
      this.setClusterURLReportViewer(this.$route.params.clusterId);
    }
    this.openReport()
    if (this.isAutoSaveReportViewer) {
      await this.$nextTick()
      await this.$nextTick()
      this.setAutoSaveReportViewer(false)
      this.saveReport(autosaving_Message)
    }
  },
  methods: {
    ...mapActions([
      'setDragModeReportViewer',
      'setCurrentReportViewer',
      'cleanCacheReportViewer',
      'setForceUpdateReportViewer',
      'addMessageTerminalSection',
      'toggleReportListVisReportViewer',
      'setForceUpdReprtsTableReportViewer',
      'setMessageTextReportViewer',
      'setMessageVisibleReportViewer',
      'setReportModifiedReportViewer',
      'resetReportModifiedReportViewer',
      'setSaveLayoutReportViewer',
      'setReportStatesListReportViewer',
      'readSingleReportMiddleware',
      'updateReportMiddleware',
      'downloadRawFilesMiddleware',
      'setContextOnHover',
      'setOriginalConfigurationReportViewer',
      'setScanFilesListReportViewer',
      'setMappingSetReportViewer',
      'setAutoSaveReportViewer',
      'setForceReadFromDBReportViewer',
      'sendResourceAccessRequestMiddleware',
      'readClusterOwnerDataMiddleware',
      'readUserFullNameMiddlewareAuth',
      'setReportURLReportViewer',
      'setProjectURLReportViewer',
      'setClusterURLReportViewer',
    ]),
    handleScroll() {
      const container = this.$refs.contentScrollable;
      this.isBackToTopVisible = container.scrollTop > this.getWidthWindowMainApp / 10;
    },
    scrollToTop() {
      const container = this.$refs.contentScrollable;
      container.scrollTo({ top: 0, behavior: 'smooth' });
    },
    onHover(route) {
      this.setContextOnHover(route)
    },
    onLeave() {
      this.setContextOnHover(null)
    },
    onCardsReloaded() {
      this.$refs.reportData.refreshInformation()
    },
    async openReport() {
      if (this.$route.params.reportId !== 'undefined' && this.$route.params.reportId !== '') {
        this.routeList[0] = this.$route.params.clusterId
        this.routeList[1] = this.$route.params.projectId
        this.routeList[2] = this.$route.params.reportId
      }
      if (typeof (this.$route.params.reportId) === 'undefined' || this.$route.params.reportId === '' || this.$route.params.reportId === null) {
        this.state = this.CLEAN_STATE
        this.cleanCacheReportViewer()
        this.setCurrentReportViewer({})
        return
      }
      this.setMessageVisibleReportViewer(true)
      this.setMessageTextReportViewer(loading_Message)
      const currentReport = this.$route.params.reportId
      const currentProject = this.$route.params.projectId
      const currentCluster = this.$route.params.clusterId
      const recoverReport = !this.isForceReadFromDBReportViewer && typeof (this.getCurrentReportViewer) !== 'undefined' && typeof (this.getCurrentReportViewer.id) !== 'undefined' && this.getCurrentReportViewer.id === currentReport && typeof (this.getCurrentReportViewer.projectId) !== 'undefined' && this.getCurrentReportViewer.projectId === currentProject
      if (recoverReport) {
        this.state = this.REPORT_STATE
        this.setMessageVisibleReportViewer(false)
        window.document.title = `djuli - ${this.getCurrentReportViewer.name}`
      } else {
        this.cleanCacheReportViewer()
        this.setSaveLayoutReportViewer(false)
        this.resetReportModifiedReportViewer()

        const reportObjt = await this.readSingleReportMiddleware({
          clusterId: currentCluster,
          projectId: currentProject,
          reportId: currentReport,
        })
        if (typeof (reportObjt) !== 'undefined' && reportObjt !== null) {
          if (!reportObjt.notHomogenize)
            homogenizeReport(reportObjt);
          ReportInfo.getConfigFromBackendPayload(this, reportObjt);
          if (reportObjt.type !== Comparison_Type) {
            const statesReport = await ReportInfo.getStatesList(reportObjt)
            this.setReportStatesListReportViewer(statesReport)
            const scanFilesList = await getScanFilesList(reportObjt)
            this.setScanFilesListReportViewer(scanFilesList)
            this.setMappingSetReportViewer(ReportInfo.getMapping(reportObjt))
            this.setOriginalConfigurationReportViewer(JSON.parse(JSON.stringify(reportObjt.configuration)))
          }
          this.setCurrentReportViewer(reportObjt)
          this.state = this.REPORT_STATE
          window.document.title = `djuli - ${reportObjt.name}`
        } else if (this.getReportAccessTypeMiddleware
            && this.getReportAccessTypeMiddleware.code === 403
            && this.getReportAccessTypeMiddleware.accesstype === false
        ) {
          this.setCurrentReportViewer({});
          this.state = this.NO_PERMISSION;
          this.setOriginalConfigurationReportViewer(null);
        } else {
          this.setCurrentReportViewer({});
          this.state = this.NOT_EXISTS_STATE;
          this.setOriginalConfigurationReportViewer(null);
        }
        this.setAutoSaveReportViewer(false)
        this.setMessageVisibleReportViewer(false)
        this.setForceReadFromDBReportViewer(false)
      }
      if (!(ExtraConfigKey in this.getCurrentReportViewer)) this.getCurrentReportViewer[ExtraConfigKey] = {};
      this.showTools = typeof (this.getCurrentReportViewer[ExtraConfigKey][NameExtraConfig.ThirdPartyBackEnd]) === 'undefined'
      this.editMode = (this.getCurrentReportViewer.accesstype || false) && this.editMode
      await this.updateSidebar()
    },
    reportSelectedSidebar() {
      this.state = this.CLEAN_STATE
      this.cleanCacheReportViewer()
      this.setCurrentReportViewer({})
      this.openReport()
    },
    projectDisabled() {
      this.state = this.CLEAN_STATE
      this.cleanCacheReportViewer()
      this.setCurrentReportViewer({})
    },
    downloadProcessedData() {
      try {
        this.setMessageVisibleReportViewer(true)
        this.setMessageTextReportViewer(exportingProcData_Message)
        const informationFile = []
        const WorkBook = XLSX.utils.book_new();
        let workEmpty = true
        const usedSheetNames = new Set()
        for (let graph of Object.values(this.getCurrentReportViewer.graphs)) {
          for (let [keyData, data] of Object.entries(graph.dataGraph)) {
            for (let [keyStructure, structure] of Object.entries(data)) {
              if (structure.type === 'table' && structure.dataTable.length > 0) {
                const ws_data = [structure.headerOrder]
                let headName = keyData
                if (headName.length > 15) {
                  headName = headName.substring(0, 15)
                }
                let ws_name = `${headName}-${keyStructure}`
                if (ws_name.length > 27) {
                  ws_name = ws_name.substring(0, 27)
                }
                let repeatedCounter = 1
                let formatted_ws_name = ws_name
                while (usedSheetNames.has(formatted_ws_name)) {
                  formatted_ws_name = `${ws_name} (${repeatedCounter})`
                  repeatedCounter++
                }
                for (let j = 0; j < structure.dataTable.length; j++) {
                  const dataRow = []
                  for (let k = 0; k < structure.headerOrder.length; k++) {
                    dataRow.push(structure.dataTable[j][structure.headerOrder[k]])
                  }
                  ws_data.push(dataRow)
                }
                const ws = XLSX.utils.aoa_to_sheet(ws_data);
                usedSheetNames.add(formatted_ws_name)
                XLSX.utils.book_append_sheet(WorkBook, ws, formatted_ws_name);
                informationFile.push([formatted_ws_name, ws_data])
                workEmpty = false
              }
            }
          }
        }
        if (workEmpty) {
          const errText = 'The report does not contain any table to export'
          const errTitle = 'No analysis to export'
          this.$notifTerm.addNotification(this.$notifTerm.typesNotif.ERROR, errTitle, errText, zoneNotify)
          return
        }
        XLSX.writeFile(WorkBook, `${this.getCurrentReportViewer.name} Processed Data.xlsb`)
      } catch (error) {
        const errText = 'Error while exporting the processed data. Please try again in a few minutes. ' + error
        const errTitle = 'Error packing analysis'
        this.$notifTerm.addNotification(this.$notifTerm.typesNotif.ERROR, errTitle, errText, zoneNotify)
      } finally {
        this.setMessageVisibleReportViewer(false)
      }
    },
    resetGridLayout() {
      this.getCurrentReportViewer.layout = {}
      if (typeof (this.getCurrentReportViewer.viewConfiguration) !== 'undefined') {
        this.getCurrentReportViewer.viewConfiguration.hidenElements = {}
      }
      this.setSaveLayoutReportViewer(true)
      this.setReportModifiedReportViewer()
      this.setForceUpdateReportViewer(2)
    },
    async forceUpdateReportViewer() {
      this.state = this.CLEAN_STATE
      if (this.getCurrentReportViewer.type !== Comparison_Type) {
        const statesReport = await ReportInfo.getStatesList(this.getCurrentReportViewer)
        this.setReportStatesListReportViewer(statesReport)
        const scanFilesList = await getScanFilesList(this.getCurrentReportViewer)
        this.setScanFilesListReportViewer(scanFilesList)
        this.setMappingSetReportViewer(ReportInfo.getMapping(this.getCurrentReportViewer))
      }
      this.$nextTick(() => {
        this.state = this.REPORT_STATE
      })
    },
    async recoverReport() {
      this.state = this.CLEAN_STATE
      this.setMessageVisibleReportViewer(true)
      this.setMessageTextReportViewer(loading_Message)
      this.setSaveLayoutReportViewer(false)
      this.resetReportModifiedReportViewer()
      const currentReport = this.$route.params.reportId
      const currentProject = this.$route.params.projectId
      const currentCluster = this.$route.params.clusterId

      const reportObjt = await this.readSingleReportMiddleware({
        clusterId: currentCluster,
        projectId: currentProject,
        reportId: currentReport,
      })
      if (reportObjt === null || typeof (reportObjt) === 'undefined') {
        if (this.getReportAccessTypeMiddleware.code === 403 && this.getReportAccessTypeMiddleware.accesstype === false) {
          this.state = this.NO_PERMISSION;
        } else {
          const errTitle = 'Error retrieving report'
          const errText = 'Error retrieving the report from the server. Try again in a few minutes.'
          const errZone = 'Report Viewer'
          this.$notifTerm.addNotification(this.$notifTerm.typesNotif.ERROR, errTitle, errText, errZone)
          return
        }
      }

      this.cleanCacheReportViewer()
      if (typeof (reportObjt) !== 'undefined' && reportObjt !== null) {
        if (!reportObjt.notHomogenize)
          homogenizeReport(reportObjt);
        ReportInfo.getConfigFromBackendPayload(this, reportObjt);
        if (reportObjt.type !== Comparison_Type) {
          const statesReport = await ReportInfo.getStatesList(reportObjt)
          this.setReportStatesListReportViewer(statesReport)
          const scanFilesList = await getScanFilesList(reportObjt)
          this.setScanFilesListReportViewer(scanFilesList)
          this.setMappingSetReportViewer(ReportInfo.getMapping(reportObjt))
        }
        await this.$nextTick()
        this.setCurrentReportViewer(reportObjt)
        this.state = this.REPORT_STATE
        window.document.title = `djuli - ${reportObjt.name}`
        await this.$nextTick()
        await this.$nextTick()
      } else {
        this.setCurrentReportViewer({})
        this.state = this.NOT_EXISTS_STATE
      }
      this.resetReportModifiedReportViewer()
      this.setMessageVisibleReportViewer(false)
    },
    async saveReport(message = 'Updating report...') {
      try {
        this.setMessageVisibleReportViewer(true)
        this.setMessageTextReportViewer(message)
        this.resetReportModifiedReportViewer()
        if (this.isSaveLayoutReportViewer) {
          this.$refs.reportBoardElem.saveLayout()
        }
        const currentCluster = this.$route.params.clusterId
        const reponse = await this.updateReportMiddleware({
          clusterId: currentCluster,
          report: this.getCurrentReportViewer
        })
        if (!(reponse)) {
          this.setReportModifiedReportViewer()
        } else {
          this.setForceUpdReprtsTableReportViewer(true)
          await this.updateSidebar()
        }
        window.document.title = `djuli - ${this.getCurrentReportViewer.name}`
        return true
      } catch (error) {
        this.setReportModifiedReportViewer()
        const errTitle = 'Error updating the report'
        const errText = 'Error updating the report in the server. Try again in a few minutes.'
        this.$notifTerm.addNotification(this.$notifTerm.typesNotif.ERROR, errTitle, errText, zoneNotify)
        return false
      } finally {
        this.setMessageVisibleReportViewer(false)
      }
    },
    async userSaveReport() {
      if (!(await ReportInfo.verifyReport(this, this.getCurrentReportViewer))) {
        return
      }
      await this.saveReport()
    },
    async showPopup() {
      this.showingPopup = true;
      const ownerData = await this.readClusterOwnerDataMiddleware({ clusterId: this.$route.params.clusterId })
      this.receiver = ownerData.email;
    },
    async closePopup() {
      this.showingPopup = false;
    },
    isValidEmail(inputValue) {
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailPattern.test(inputValue);
    },
    async sendAccessRequest() {
      try {
        this.isLoading = true;
        const ownerData = await this.readClusterOwnerDataMiddleware({ clusterId: this.$route.params.clusterId })
        this.receiver = ownerData.email;
        const success = await this.readUserFullNameMiddlewareAuth({ email: auth.currentUser.email });
        let clusterId = null;
        let projectId = null;
        let reportId = null;
        let entityId = null;
        if (success && this.receiver && this.isValidEmail(this.receiver)) {
          const userData = this.getUserNameMiddlewareAuth;
          if (this.resourceType === 'Cluster') {
            clusterId = this.$route.params.clusterId;
            projectId = null;
            reportId = null;
            entityId = clusterId;
          } else if (this.resourceType === 'Project') {
            clusterId = this.$route.params.clusterId;
            projectId = this.$route.params.projectId;
            reportId = null;
            entityId = projectId;
          } else {
            clusterId = this.$route.params.clusterId;
            projectId = this.$route.params.projectId;
            reportId = this.$route.params.reportId;
            entityId = reportId;
          }
          const subject = `Request For Sharing the ${entityId} resource`;
          const encryptedName = userData.displayName;
          const encryptedEmail = userData.email;
          const secretKey = process.env.VUE_APP_ENCRYPTION_SECRET_KEY;
          const displayName = AES.decrypt(encryptedName, secretKey).toString(Utf8);
          const email = AES.decrypt(encryptedEmail, secretKey).toString(Utf8);
          const sender = { email: email || 'Email', name: displayName || 'Someone' };
          const message = this.messageBody;
          const linkObj = {
            clusterId: clusterId,
            projectId: projectId,
            reportId: reportId,
            entityId: entityId
          }
          let sendSuccess = false;
          if (sender !== null && sender !== 'undefined' && subject !== null && this.receiver !== null && this.receiver !== 'undefined' && this.resourceType !== null && linkObj !== null && linkObj !== 'undefined') {
            sendSuccess = await this.sendResourceAccessRequestMiddleware({
              sender: sender,
              subject: subject,
              receiver: this.receiver,
              message: message,
              resourceType: this.resourceType,
              entityName: linkObj.entityId,
              linkObj: linkObj
            })
          } else {
            this.$alert(
              'There is a problem communicating with the server. Please try again!',
              'Server Error!',
              'error',
            );
          }
          if (sendSuccess) {
            if (sendSuccess.data.responseCreateRequest.success && sendSuccess.data.responseSendEmail.success) {
              this.$alert(
                `${sendSuccess.data.responseCreateRequest.message} and ${sendSuccess.data.responseSendEmail.message}`,
                'Request Successful!',
                'success',
              );
            } else if (!sendSuccess.data.responseCreateRequest.success && sendSuccess.data.responseSendEmail.success) {
              this.$alert(
                sendSuccess.data.responseCreateRequest.message,
                'Creating Request Failed!',
                'error',
              );
            } else if (sendSuccess.data.responseCreateRequest.success && !sendSuccess.data.responseSendEmail.success) {
              this.$alert(
                sendSuccess.data.responseSendEmail.message,
                'Sending Email Failed!',
                'error',
              );
            } else {
              this.$alert(
                `${sendSuccess.data.responseCreateRequest.message} and ${sendSuccess.data.responseSendEmail.message}`,
                'Request Failed!',
                'error',
              );
            }
          } else {
            this.$alert(
              'Internal error, please try in a few seconds',
              'Request Failed',
              'error',
            );
          }
        } else {
          if (!this.isValidEmail(this.receiver)) {
            this.emailValid = false;
            this.showingPopup = true;
          } else {
            this.$alert(
              `Either the user doesn't exist or you are not authorized to request the ${this.getCurrentReportViewer?.name} resource to the user!`,
              'Unathorized To Request',
              'error',
            );
            return;
          }
        }
        this.closePopup();
        this.email = '';
        this.message = '';
        this.emailValid = true;
      } catch (error) {
        const errTitle = 'Error creating the access request'
        const errText = 'Error creating the request in the server. Try again in a few minutes.'
        this.$notifTerm.addNotification(this.$notifTerm.typesNotif.ERROR, errTitle, errText, zoneNotify)
        return false
      } finally {
        this.isLoading = false
      }
    },
  }
}
</script>

<style lang="scss" scoped>
.content-col {
  overflow-y: auto;
  height: 100%;
}

.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 1s cubic-bezier(0.5, 0, 0.5, 1);
}

.fade-slide-enter, .fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-30px);
}

.row-data-report {
  border: black solid 2px;
  box-shadow: 5px 5px 10px 2px #ccc;
  border-radius: 10px;
}

::v-deep .dropdown-item {
  font-size: 0.8rem;
}

.configuration-window {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 101;
}

.welcome-mssg {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 22rem
}

.djuliLogo-mssg {
  width: 15rem;
  margin-left: 3rem;
  opacity: 1
}

.spinner-message {
  height: 1.5rem;
  width: 1.5rem;
}

.message-text {
  font-size: 1.125rem;
  text-align: left;
}

.message-text-report {
  font-size: 1.125rem;
}

.signature-wrapper {
  position: absolute;
  top: 1rem;
  right: 5rem;
}

.signature-area {
  width: 8rem;
  height: 4rem;
  border: solid 1.5px #777;
  border-radius: 5px;
}

.signature-line {
  width: 6.5rem;
  height: 3.7rem;
  border-bottom: solid 1.55px #000;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  position: absolute;
}

.signature-text {
  color: #555;
  margin: 0px;
  padding: 0px;
  font-size: 0.8rem;
  font-family: roboto;
}

.export-window {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 103;
}

::v-deep .back-to-top {
  z-index: 110;
}

#elemNotFoundImg {
  border-radius: 20px;
  margin: 2rem;
}

.welcome-message-header {
  font-size: 2rem;
  font-weight: bold;
  margin-bottom: 0.5rem;
}

.no-report-header {
  font-size: 1.5rem;
  font-weight: bold;
  margin-bottom: 0.25rem;
}

.breadcrumb-link {
  text-decoration: underline;
}

.notify-card-body {
  background-color: #4DBF45;
}

.custom-card {
  border-radius: 15px;
  overflow: hidden;
  background-color: rgba(0, 0, 0, 0.03);
}

.loading-report-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  width: 100%;
  max-width: 33vw;
  margin: 0 auto;
  padding: 20px;
  box-sizing: border-box;
}
</style>
